import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { format, parseISO } from "date-fns";
import { History, CheckCircle2, AlertCircle, User, RefreshCw } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { auditAPI } from "../../../../services/api";

/**
 * Activity Log Widget
 * Displays timeline of all DSR-related actions from audit trail
 * Filters by entity_type "dsr_v2" to show all DSR module activities
 * Shows up to 6 most recent entries with timestamp, action, and performer
 */
export function ActivityLogWidget() {
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        setLoading(true);
        // Fetch audit logs filtered by resource_type "dsr_v2"
        const response = await auditAPI.getAll(null, 100, 0, "dsr_v2");

        // Handle response structure: { data: [...], total, skip, limit, hasMore }
        if (response.data) {
          // Check if response has nested 'data' array (paginated response)
          const logs = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];

          console.log("Activity logs fetched:", logs.length, "entries");
          setActivityLog(logs);
        }
      } catch (error) {
        console.error("Failed to fetch activity log:", error);
        setActivityLog([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center gap-2 py-6">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <p className="text-sm text-muted-foreground">Loading activity...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!activityLog || activityLog.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-6">No activity recorded yet</p>
        </CardContent>
      </Card>
    );
  }

  // Show last 6 entries, most recent first
  const recentEntries = [...activityLog].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 6);

  const getActionDescription = (entry) => {
    // Convert raw action to plain-language description
    const action = entry.action;
    if (action === "create") {
      return "Created DSR request";
    }
    if (action === "update") {
      return "Updated DSR request";
    }
    if (action === "delete") {
      return "Deleted DSR request";
    }
    return action;
  };

  const getActionIcon = (action) => {
    if (action === "create") {
      return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
    }
    if (action === "update") {
      return <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    }
    if (action === "delete") {
      return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
    }
    return <History className="h-4 w-4 text-slate-500 dark:text-slate-400" />;
  };

  const getActionBgColor = (action) => {
    if (action === "create") {
      return "bg-green-50 dark:bg-green-950/30";
    }
    if (action === "update") {
      return "bg-blue-50 dark:bg-blue-950/30";
    }
    if (action === "delete") {
      return "bg-red-50 dark:bg-red-950/30";
    }
    return "bg-slate-50 dark:bg-slate-800/30";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          Activity Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {recentEntries.map((entry, index) => (
            <div key={index} className={cn("p-3 rounded-lg border border-transparent", getActionBgColor(entry.action))}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">{getActionIcon(entry.action)}</div>
                <div className="flex-1 min-w-0">
                  {/* DSR Reference */}
                  {entry.resource_reference && (
                    <p className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      {entry.resource_reference}
                    </p>
                  )}
                  {/* Plain-language action description */}
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {getActionDescription(entry)}
                  </p>
                  {/* Details if available */}
                  {entry.details && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {typeof entry.details === "object"
                        ? JSON.stringify(entry.details).substring(0, 100)
                        : entry.details}
                    </p>
                  )}
                  {/* User and timestamp */}
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3" />
                    <span>{entry.user_email}</span>
                    <span>•</span>
                    <span>{format(parseISO(entry.timestamp), "dd MMM yyyy HH:mm")}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {activityLog.length > 6 && (
          <div className="pt-3 border-t mt-3">
            <p className="text-xs text-muted-foreground text-center">
              +{activityLog.length - 6} more {activityLog.length - 6 === 1 ? "entry" : "entries"}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ActivityLogWidget;
